import { c as _c } from "react/compiler-runtime";
import { Alert, Box, Center, Spinner, Stack, Text } from '@chakra-ui/react';
import { heldTermByProductId, memberDefaultTermId } from '@src/components/sco/UpgradeForm';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { PaywallProductGrid } from '../PaywallProductGrid';
import { PendingPlanChangeBanner } from '../PendingPlanChangeBanner';
import { ADVISOR_TEAM_PRODUCT_ID, STARTUPS_PRO_PRODUCT_ID, type PaywallIntentId } from '../intent';
import { conversionCheckoutHref, PLAN_CHANGE_AT_RENEWAL, readConversionCheckoutParams } from './checkoutParams';
import { useConversionProducts } from './useConversionProducts';
import { useMembershipPlanChange } from './useMembershipPlanChange';
const LADDER = [STARTUPS_PRO_PRODUCT_ID, ADVISOR_TEAM_PRODUCT_ID];
export type ConversionPlansProps = {
  /** Intent when the URL does not carry one (e.g. `investor-matching-plans` → a fundraising intent). */
  defaultIntent?: PaywallIntentId;
};

/**
 * Plan chooser (`?prompt=plans`): the membership ladder from the live catalog. Choosing a plan
 * swaps the dialog to the Conversion checkout for that product, keeping the page underneath.
 */
export const ConversionPlans = t0 => {
  const $ = _c(52);
  const {
    defaultIntent: t1
  } = t0;
  const defaultIntent = t1 === undefined ? "default" : t1;
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  let t2;
  if ($[0] !== params) {
    t2 = readConversionCheckoutParams(params);
    $[0] = params;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const request = t2;
  const intent = request.intent || defaultIntent;
  const {
    currentUser
  } = useCurrentUser();
  let t3;
  if ($[2] !== currentUser || $[3] !== request.termId) {
    t3 = request.termId || memberDefaultTermId(currentUser) || "month";
    $[2] = currentUser;
    $[3] = request.termId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const termId = t3;
  let t4;
  if ($[5] !== request.couponCode || $[6] !== termId) {
    t4 = {
      termId,
      couponCode: request.couponCode
    };
    $[5] = request.couponCode;
    $[6] = termId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    products,
    loading,
    error
  } = useConversionProducts(LADDER, t4);
  currentUser?.subscriptions;
  let t5;
  if ($[8] !== currentUser?.subscriptions) {
    t5 = (currentUser?.subscriptions || []).filter(_temp).map(_temp2).filter(_temp3);
    $[8] = currentUser?.subscriptions;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const activeProductIds = t5;
  let t6;
  if ($[10] !== activeProductIds) {
    t6 = activeProductIds.includes(ADVISOR_TEAM_PRODUCT_ID);
    $[10] = activeProductIds;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const onTeam = t6;
  let t7;
  if ($[12] !== activeProductIds || $[13] !== onTeam) {
    t7 = onTeam ? [ADVISOR_TEAM_PRODUCT_ID] : activeProductIds;
    $[12] = activeProductIds;
    $[13] = onTeam;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const currentProductIds = t7;
  let t8;
  if ($[15] !== currentProductIds || $[16] !== currentUser || $[17] !== termId) {
    t8 = heldTermByProductId(currentUser, currentProductIds, termId);
    $[15] = currentProductIds;
    $[16] = currentUser;
    $[17] = termId;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const currentTermByProductId = t8;
  const t9 = activeProductIds.length === 0;
  let t10;
  if ($[19] !== t9) {
    t10 = {
      skip: t9
    };
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const planChange = useMembershipPlanChange(t10);
  let t11;
  if ($[21] !== activeProductIds || $[22] !== onTeam || $[23] !== planChange) {
    t11 = onTeam ? planChange.offerProductIds : LADDER.filter(productId => !activeProductIds.includes(productId));
    $[21] = activeProductIds;
    $[22] = onTeam;
    $[23] = planChange;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const eligibleProductIds = t11;
  if (loading && products.length === 0) {
    let t12;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Center minH="20rem" w={{
        base: "full",
        md: "56rem"
      }}><Spinner aria-label="Loading plans" /></Center>;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    return t12;
  }
  if (error || products.length === 0) {
    let t12;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Box maxW="full" p={8} w="28rem"><Alert.Root status="error"><Alert.Indicator /><Text>Plans aren’t available right now.</Text></Alert.Root></Box>;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    return t12;
  }
  let t12;
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      base: 5,
      md: 8
    };
    t13 = {
      base: "full",
      md: "56rem"
    };
    $[27] = t12;
    $[28] = t13;
  } else {
    t12 = $[27];
    t13 = $[28];
  }
  let t14;
  if ($[29] !== planChange) {
    t14 = planChange.pending ? <PendingPlanChangeBanner error={planChange.keepError} keeping={planChange.keeping} pending={planChange.pending} onKeep={() => planChange.keepCurrentPlan()} /> : null;
    $[29] = planChange;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = onTeam ? planChange.ctaLabels : undefined;
  const t16 = request.tokens || undefined;
  let t17;
  if ($[31] !== intent || $[32] !== params || $[33] !== pathname || $[34] !== planChange.offerProductIds || $[35] !== request.couponCode || $[36] !== request.termId || $[37] !== request.tokens || $[38] !== router) {
    t17 = product => {
      if (!product.productId) {
        return;
      }
      router.push(conversionCheckoutHref(pathname || "/", params?.toString(), {
        productId: product.productId,
        intent,
        tokens: request.tokens,
        termId: request.termId,
        couponCode: request.couponCode,
        planChange: planChange.offerProductIds.includes(product.productId) ? PLAN_CHANGE_AT_RENEWAL : null
      }), undefined, {
        scroll: false,
        shallow: true
      });
    };
    $[31] = intent;
    $[32] = params;
    $[33] = pathname;
    $[34] = planChange.offerProductIds;
    $[35] = request.couponCode;
    $[36] = request.termId;
    $[37] = request.tokens;
    $[38] = router;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== currentProductIds || $[41] !== currentTermByProductId || $[42] !== eligibleProductIds || $[43] !== intent || $[44] !== products || $[45] !== t15 || $[46] !== t16 || $[47] !== t17) {
    t18 = <PaywallProductGrid assurance="Cancel anytime" ctaLabels={t15} currentProductIds={currentProductIds} currentTermByProductId={currentTermByProductId} eligibleProductIds={eligibleProductIds} intent={intent} products={products} showPlanSummary={false} tokens={t16} onContinue={t17} />;
    $[40] = currentProductIds;
    $[41] = currentTermByProductId;
    $[42] = eligibleProductIds;
    $[43] = intent;
    $[44] = products;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t14 || $[50] !== t18) {
    t19 = <Stack gap={5} maxW="full" p={t12} w={t13}>{t14}{t18}</Stack>;
    $[49] = t14;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  return t19;
};
function _temp(subscription) {
  return subscription?.isActive;
}
function _temp2(subscription_0) {
  return subscription_0?.productId || subscription_0?.product?.productId || "";
}
function _temp3(id) {
  return LADDER.includes(id);
}